import { Button } from "@/components/ui/button";
import {
  CHROME_WEB_STORE_URL,
  detectChromeExtension,
} from "@/lib/chrome-extension";
import { t } from "i18next";
import {
  AlarmClockCheckIcon,
  Check,
  FileText,
  LinkIcon,
  Monitor,
  MousePointerClickIcon,
  Puzzle,
  RocketIcon,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";

type ExtensionStatus = "checking" | "installed" | "notInstalled";

function statusTitleKey(
  status: ExtensionStatus
):
  | "settings.chromeExtension.status.checking"
  | "settings.chromeExtension.status.installed"
  | "settings.chromeExtension.status.notInstalled" {
  if (status === "checking") return "settings.chromeExtension.status.checking";
  if (status === "installed")
    return "settings.chromeExtension.status.installed";
  return "settings.chromeExtension.status.notInstalled";
}

export const ChromeExtension = (): JSX.Element => {
  const [status, setStatus] = useState<ExtensionStatus>("checking");
  const [version, setVersion] = useState<string | undefined>();

  const refreshStatus = useCallback((): void => {
    detectChromeExtension()
      .then((result) => {
        setStatus(result.installed ? "installed" : "notInstalled");
        setVersion(result.version);
      })
      .catch(() => {
        setStatus("notInstalled");
        setVersion(undefined);
      });
  }, []);

  useEffect(() => {
    refreshStatus();

    const onVisible = (): void => {
      if (document.visibilityState === "visible") {
        refreshStatus();
      }
    };
    const onFocus = (): void => {
      refreshStatus();
    };

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return (): void => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [refreshStatus]);

  return (
    <section>
      <div className="space-y-5">
        <p className="text-base">{t("settings.chromeExtension.subtitle")}</p>

        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center gap-[7px] rounded-full bg-[#E6F3FD] px-[17px] py-[5px] text-base font-semibold text-black/70">
            <MousePointerClickIcon className="size-[19px] stroke-[#0399FF]" />
            {t("settings.chromeExtension.features.oneClick")}
          </span>
          <span className="inline-flex items-center gap-[7px] rounded-full bg-[#E6F3FD] px-[17px] py-[5px] text-base font-semibold text-black/70">
            <LinkIcon className="size-[19px] stroke-[#0399FF]" />
            {t("settings.chromeExtension.features.emrSupport")}
          </span>
          <span className="inline-flex items-center gap-[7px] rounded-full bg-[#E6F3FD] px-[17px] py-[5px] text-base font-semibold text-black/70">
            <AlarmClockCheckIcon className="size-[19px] stroke-[#0399FF]" />
            {t("settings.chromeExtension.features.saveTime")}
          </span>
          <span className="inline-flex items-center gap-[7px] rounded-full bg-[#E6F3FD] px-[17px] py-[5px] text-base font-semibold text-black/70">
            <RocketIcon className="size-[19px] stroke-[#0399FF]" />
            {t("settings.chromeExtension.features.quickSetup")}
          </span>
        </div>

        <div className="space-y-[10px] rounded-[15px] border border-[#E6E6E6] p-5">
          <h3 className="text-base font-bold">
            {t("settings.chromeExtension.status.title")}
          </h3>
          <div className="flex items-start gap-[19px]">
            <div
              className={`flex h-[42px] w-[42px] items-center justify-center rounded-full ${
                status === "installed" ? "bg-[#E6F7ED]" : "bg-[#E6E6E6]"
              }`}
            >
              {status === "installed" ? (
                <Check className="h-[25.2px] w-[27.3px] text-[#1B7F4E]" />
              ) : (
                <Puzzle className="h-[25.2px] w-[27.3px] text-[#8A8A8A]" />
              )}
            </div>
            <div className="flex flex-col gap-[4px]">
              <p className="text-base font-medium">
                {t(statusTitleKey(status))}
              </p>
              <p className="text-sm text-[#545454]">
                {status === "installed"
                  ? t("settings.chromeExtension.status.installedDesc", {
                      version: version ? ` (v${version})` : "",
                    })
                  : t("settings.chromeExtension.status.notInstalledDesc")}
              </p>
            </div>
          </div>
          {status !== "installed" && (
            <Button onClick={() => window.open(CHROME_WEB_STORE_URL, "_blank")}>
              {t("settings.chromeExtension.status.installButton")}
            </Button>
          )}
        </div>

        <div className="space-y-[10px] rounded-[15px] border border-[#E6E6E6] p-5">
          <h3 className="text-base font-bold">
            {t("settings.chromeExtension.howItWorks.title")}
          </h3>
          <div className="flex justify-between gap-4">
            <div className="flex flex-1 flex-col items-center gap-[10px]">
              <div className="flex h-[75px] w-[89px] items-center justify-center rounded-lg bg-muted">
                <Monitor className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="flex flex-col items-center gap-[4px]">
                <p className="text-base font-bold">
                  {t("settings.chromeExtension.howItWorks.step1.title")}
                </p>
                <p className="text-center text-sm text-[#545454]">
                  {t("settings.chromeExtension.howItWorks.step1.desc")}
                </p>
              </div>
            </div>
            <div className="flex flex-1 flex-col items-center gap-[10px]">
              <div className="flex h-[75px] w-[99px] items-center justify-center rounded-lg bg-muted">
                <Puzzle className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="flex flex-col items-center gap-[4px]">
                <p className="text-base font-bold">
                  {t("settings.chromeExtension.howItWorks.step2.title")}
                </p>
                <p className="text-center text-sm text-[#545454]">
                  {t("settings.chromeExtension.howItWorks.step2.desc")}
                </p>
              </div>
            </div>
            <div className="flex flex-1 flex-col items-center gap-[10px]">
              <div className="flex h-[77px] w-[95px] items-center justify-center rounded-lg bg-muted">
                <FileText className="h-8 w-8 text-muted-foreground" />
              </div>
              <div className="flex flex-col items-center gap-[4px]">
                <p className="text-base font-bold">
                  {t("settings.chromeExtension.howItWorks.step3.title")}
                </p>
                <p className="text-center text-sm text-[#545454]">
                  {t("settings.chromeExtension.howItWorks.step3.desc")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
